import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/** UI.md §6: primary is near-black; secondary and pills are white with a hairline. */
const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border border-transparent font-semibold whitespace-nowrap transition-colors duration-150 outline-none select-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:pointer-events-none disabled:opacity-40 aria-disabled:pointer-events-none aria-disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        outline: "border-border bg-card text-foreground-secondary hover:border-border-strong hover:text-foreground aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-primary-foreground",
        secondary: "border-border bg-card text-foreground-secondary hover:border-border-strong hover:text-foreground",
        ghost: "text-foreground-secondary hover:bg-hover hover:text-foreground",
        destructive: "bg-negative-soft text-negative hover:border-negative/30",
        link: "h-auto border-0 px-0 font-medium text-brand underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 px-3 text-body",
        sm: "h-7 px-2.5 text-label",
        xs: "h-6 px-2 text-label",
        lg: "h-9 px-4 text-body",
        icon: "size-8",
        "icon-sm": "size-7",
        "icon-xs": "size-6",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
